import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import {
  FormField,
  Input,
  Button,
  Heading,
  Text,
  Form,
} from '@livechat/design-system-react-components';

import Config from 'Config';
import { StandardError } from 'types/error';
import { Layout } from 'components/layout/Layout';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { ErrorMessageForm } from 'components/forms/error-message-form/ErrorMessageForm';
import { PasswordRequirements } from 'components/password-requirements/PasswordRequirements';
import { FormStates, SigninFormData } from 'types/form';
import { fetchTokenDetails } from 'hooks/use-get-token-details';
import Location from 'services/Location';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { detectProduct, getAppLinkByName, getProductName } from 'helpers/product';
import { validatePassword } from 'helpers/validators/password';
import { submitPost } from 'helpers/form';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import Amplitude from 'services/Amplitude';
import { ResetPasswordSent } from 'routes/forgot-password/ResetPasswordSent';
import { getAccessToken } from 'helpers/account';
import { accountsClient } from 'services/connectivity/accounts/client';

import * as styles from './styles';

const getToken = () => {
  const token = getAccessToken();

  return decodeURIComponent(Location.getNthUrlParam(2)) || (token && decodeURIComponent(token));
};

export const SetPassword: FC = () => {
  const [isTokenValid, setIsTokenValid] = useState<boolean>(true);
  const [requestError, setRequestError] = useState<StandardError | null>(null);
  const [resendRequestSuccess, setResendRequestSuccess] = useState<boolean | null>(null);
  const [submitRequestSuccess, setSubmitRequestSuccess] = useState<boolean | null>(null);
  const [formState, setFormState] = useState<string>(FormStates.Default);
  const [formData, setFormData] = useState<SigninFormData>({
    email:
      (Location.getHashParam('email') && decodeURIComponent(Location.getHashParam('email'))) ||
      decodeURIComponent(Location.getNthUrlParam(1)),
  });
  const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);

  const accessToken = getToken();

  useEffect(() => {
    if (decodeURIComponent(Location.getNthUrlParam(2))) {
      Cookies.set('access_token', decodeURIComponent(Location.getNthUrlParam(2)));
    } else {
      Cookies.set('access_token', decodeURIComponent(getAccessToken()));
    }

    const verifyAccessToken = async () => {
      try {
        await fetchTokenDetails(accessToken);
        setIsTokenValid(true);
      } catch (e) {
        setIsTokenValid(false);
        setRequestError({ description: e.message });
      }
    };

    verifyAccessToken();
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { password, email } = formData;

    const validationError = validatePassword(password);

    if (validationError) {
      setPasswordValidationError(validationError);

      return;
    }

    setFormState(FormStates.Requesting);

    const { error } = await accountsClient.resetPassword({ password }, accessToken);

    if (error) {
      if (
        error.error === 'access_denied' &&
        error.error_description === 'account has credentials authentication disabled'
      ) {
        Location.handleCustomSSORedirect();
        return;
      }

      setRequestError({
        error: error.error,
        description: error.error_description,
        requestId: error.request_id,
      });

      return;
    }

    setSubmitRequestSuccess(true);

    const product = detectProduct();
    if (product) {
      pushExternalRedirectToDataLayer(getAppLinkByName(getProductName(product)));
    }

    Amplitude.logEvent(email, 'Agents: Reset password');
    submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(Config)}`, {
      email,
      password,
    });
  };

  const handlePasswordInputChange = (e) => {
    if (!validatePassword(e.target.value)) {
      setPasswordValidationError(null);
      setFormState(FormStates.Default);
    }

    setFormData((prevState) => ({
      ...prevState,
      password: e.target.value,
    }));
  };

  const handleResendPasswordReset = async (): Promise<void> => {
    setFormState(FormStates.Requesting);

    const { error, response } = await accountsClient.resendPassword({
      email: formData.email,
    });

    if (error) {
      setRequestError({
        error: error.error,
        responseCode: response.status,
      });

      return;
    }

    setResendRequestSuccess(true);
  };

  if (resendRequestSuccess || (requestError && requestError.responseCode === 403)) {
    return <ResetPasswordSent email={formData.email} />;
  }

  if (!formData.email || !accessToken) {
    return (
      <ErrorMessageForm
        titleCaption="Incorrect link"
        titleContent={
          <>
            Your password reset link is incorrect.
            <br /> Chat with our support team to get help.
          </>
        }
        contactSupportButton
        backToLogin
      />
    );
  }

  if (!isTokenValid) {
    return (
      <ErrorMessageForm
        titleCaption="Your link expired"
        titleContent={
          <>
            Looks like the link you used to set your password has expired. Click the button below to
            receive a new link at <br />
            <b>{formData.email}</b>
          </>
        }
        buttonText="Resend link"
        backToLogin
        onClick={handleResendPasswordReset}
        loading={formState === FormStates.Requesting}
      />
    );
  }

  if (requestError) {
    return (
      <UnexpectedError description={requestError.description} requestId={requestError.requestId} />
    );
  }

  if (submitRequestSuccess) {
    return (
      <div css={styles.successContainer}>
        <Heading size="lg">All done!</Heading>
        <Text>Redirecting to the app…</Text>
      </div>
    );
  }

  return (
    <Layout showHeaderLogo showGoToSignIn>
      <div css={styles.container}>
        <Heading size="lg">Set password for your account</Heading>
        <Text>Create a password to login to</Text>
        <Heading size="xs" bold>
          {formData.email}
        </Heading>
        <Form onSubmit={handleSubmit}>
          <FormField
            labelText="Password"
            labelFor="password"
            css={styles.formField}
            error={passwordValidationError}
          >
            <Input
              type="password"
              name="password"
              placeholder="Set your password"
              inputSize="large"
              value={formData.password}
              onChange={handlePasswordInputChange}
              autoFocus
            />
          </FormField>
          <PasswordRequirements
            password={formData.password}
            showErrors={!!passwordValidationError}
          />

          <Button
            kind="primary"
            size="large"
            css={styles.button}
            onClick={handleSubmit}
            loading={formState === FormStates.Requesting}
          >
            Set password
          </Button>
        </Form>
      </div>
    </Layout>
  );
};
